import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { IAppState } from 'config/redux/reducers';
import { ISellersEntry, IShipmentsHook } from '../types/shipments.types';
import { shipmentHelperFactory } from '../helpers/shipments.helper';

const useShipments = (): IShipmentsHook => {
  const { shipments } = useSelector((state: IAppState) => state);

  const shipmentsHelper = useMemo(() => shipmentHelperFactory(shipments as any), [shipments]);

  const qCommCount = shipmentsHelper.qComm.entries.length;
  const qelecCount = shipmentsHelper.qelec.entries.length;
  const expressCount = shipmentsHelper.express.entries.length;
  const standardFoodCount = shipmentsHelper.standardFood.entries.length;
  const standardNonFoodCount = shipmentsHelper.standardNonFood.entries.length;
  const cncItemsCount = shipmentsHelper.cnc.entries.length;

  const hasClickAndCollect = cncItemsCount > 0;
  const hasQComm = qCommCount > 0;
  const hasQelec = qelecCount > 0;
  const hasExpress = expressCount > 0;
  const hasMarketplace = shipmentsHelper.marketPlace.entries.length > 0;
  const hasStandardFood = standardFoodCount > 0;
  const hasStandardNonFood = standardNonFoodCount > 0;

  // carrefourtotalItemsCount
  const carrefourItemsCount =
    qCommCount +
    qelecCount +
    expressCount +
    standardFoodCount +
    standardNonFoodCount +
    cncItemsCount;

  // shipment counts
  const carrefourShipmentCount =
    Number(hasQComm) +
    Number(hasQelec) +
    Number(hasExpress) +
    Number(hasStandardFood) +
    Number(hasStandardNonFood) +
    Number(hasClickAndCollect);
  const marketPlaceShipmentCount = shipmentsHelper.marketPlace.entries.length;
  const pharmacyItemCount = useMemo(() => {
    if (shipmentsHelper.marketPlace.entries) {
      return shipmentsHelper.marketPlace.entries.reduce(
        (acc: number, next: ISellersEntry) => acc + (next.expressCount || 0),
        0
      );
    }
    return 0;
  }, [shipmentsHelper.marketPlace]);

  const mkpShipments =
    (shipmentsHelper.marketPlace &&
      shipmentsHelper.marketPlace.sellersEntries &&
      shipmentsHelper.marketPlace.sellersEntries.length) ||
    0;

  const totalShipmentCount = carrefourShipmentCount + mkpShipments;

  return {
    shipments,

    clickAndCollect: shipmentsHelper.cnc,
    qComm: shipmentsHelper.qComm,
    qelec: shipmentsHelper.qelec,
    express: shipmentsHelper.express,
    marketplace: shipmentsHelper.marketPlace,
    standardFood: shipmentsHelper.standardFood,
    standardNonFood: shipmentsHelper.standardNonFood,
    isFoodCart: shipmentsHelper.isFoodCart,
    isNonFoodCart: shipmentsHelper.isNonFoodCart,

    hasClickAndCollect,
    hasQComm,
    hasQelec,
    hasExpress,
    hasMarketplace,
    hasStandardFood,
    hasStandardNonFood,

    hasShipmentFood: hasClickAndCollect || hasExpress || hasQComm || hasQelec || hasStandardFood,
    hasShipmentNonFood: hasMarketplace || hasStandardNonFood,

    totalShipmentCount,
    hasShipments: shipmentsHelper.allProducts.length > 0,
    isLoadedOnce: !!shipments,
    allShipments: useMemo(
      () => [
        shipmentsHelper.standardFood,
        shipmentsHelper.standardNonFood,
        shipmentsHelper.cnc,
        shipmentsHelper.qComm,
        shipmentsHelper.qelec,
        shipmentsHelper.express,
        shipmentsHelper.marketPlace,
      ],
      [shipmentsHelper]
    ),
    carrefourShipmentCount,
    marketPlaceShipmentCount,
    pharmacyItemCount,
    carrefourItemsCount,
  };
};

export default useShipments;
